import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { CanceledError } from 'axios';
import { call, delay, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from '../../../base/api';
import { Loading } from '../../../base/enums';
import { appActions } from '../../../base/redux/slices/appSlice';
import { City } from '../../../base/interfaces/config';
import { preparePhoneInputConfig } from '../../../base/utils';
import { ConfigResponse } from '../../api/interfaces';
import { recruiterApi } from '../../api';
import { recruiterAppActions } from '../slices/recruiterAppSlice';
import { baseErrorHandler } from '../../../base/redux/sagas/appSaga';

function* init() {
    try {
        yield put(appActions.setLoading(Loading.App));

        yield call(getConfig);

        yield put(recruiterAppActions.setInitialized(true));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* getConfig() {
    try {
        const {
            data: { registrationCountries, countries, areas, seo },
        }: ApiResponse<ConfigResponse> = yield call(recruiterApi.getConfig);
        const phoneInputConfig = preparePhoneInputConfig(registrationCountries);
        yield put(recruiterAppActions.getConfigSuccess({ phoneInputConfig, countries, areas, seoTags: seo }));
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* sendConfirmationCode({ payload: { email, recaptchaToken, callback } }: AnyAction) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(recruiterApi.sendVerificationEmail, { email, recaptchaToken });
        if (callback) {
            callback();
        }
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

let citySearchRequestController: AbortController | null = null;

function* citySearchDelay({ payload }: PayloadAction<string>) {
    yield delay(300);
    try {
        yield call(citySearch, payload);
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* citySearch(search: string) {
    try {
        yield put(appActions.setLoading(Loading.CityAutocomplete));
        if (citySearchRequestController) {
            citySearchRequestController.abort();
        }
        citySearchRequestController = new AbortController();
        const { data }: ApiResponse<City[]> = yield call(
            recruiterApi.findCity,
            { search },
            citySearchRequestController.signal,
        );
        yield put(recruiterAppActions.setCitySearch(data));
        yield put(appActions.setLoading(null));
        citySearchRequestController = null;
    } catch (e) {
        if (!(e instanceof CanceledError)) {
            yield put(appActions.setLoading(null));
            citySearchRequestController = null;
            console.error(e);
        }
    }
}

export function* recruiterAppSaga() {
    yield takeEvery(recruiterAppActions.init, init);
    yield takeEvery(recruiterAppActions.sendConfirmationCode, sendConfirmationCode);
    yield takeEvery(recruiterAppActions.citySearch, citySearchDelay);
}
